<template>
  <div class="form">
    <BuySellToggle v-if="buyAndSellAreEnabled" />
    <Transition name="fade" mode="out-in">
      <component
        :key="orderStore.form.mode"
        :is="currentForm"
        :initialData="initialData"
        :geoIsUK="geoIsUK"
      />
    </Transition>
  </div>
</template>
<script setup>
import {
  computed,
  onMounted,
  watch,
  shallowRef,
  ref,
  onBeforeMount,
  onUnmounted,
} from 'vue';
import { useTitle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import { useGlobalStore } from '@/stores/globalStore';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';

import useProgressBarActions from '@/composables/progress/useProgressBarActions';
import useCreateDefaultsFromApi from '@/composables/api/useCreateDefaultsFromApi';
import useCreateDefaultsFromUrl from '@/composables/api/useCreateDefaultsFromUrl';
import useOrderCreation from '@/composables/api/useOrderCreation';

import BuySellToggle from '@/components/ui/toggles/BuySellToggle.vue';
import BuyForm from '@/components/forms/BuyForm.vue';
import SellForm from '@/components/forms/SellForm.vue';
import { checkGeo } from '@/composables/helpers/useDetectedGeo';

const { resetAllProgress } = useProgressBarActions();
const { checkApiForOrderTypeAndSetMode, isAnApiOrder } = useCreateDefaultsFromApi();
const { checkUrlForOrderTypeAndSetMode, isAnUrlOrder } = useCreateDefaultsFromUrl();
const { defaultData } = useOrderCreation();
const orderStore = useOrderFormStore();
const globalStore = useGlobalStore();
const { t } = useI18n();

const initialData = ref({});
const currentForm = shallowRef(null);
const isBuy = computed(() => orderStore.isBuyMode);
const title = computed(() => (isBuy.value ? 'Banxa – Buy Crypto' : 'Banxa – Sell Crypto'));
const buyAndSellAreEnabled = computed(
  () => globalStore.getPayments?.length > 0
    && globalStore.getPayouts?.length > 0
    && !orderStore.isOrderFromMerchantApi,
);

const geoIsUK = computed(() => checkGeo('GB'));
const geoIsUS = computed(() => checkGeo('US'));

const termOfUse = computed(() => ({
    show: geoIsUS.value,
    message: t('OrderView.label.zeroHashTerm'),
}));

// order defaults can come from the url, api, or direct visit
const setDefaults = async () => {
  await globalStore.fetchPayments();

  if (isAnUrlOrder) {
    const merchDefaults = await checkUrlForOrderTypeAndSetMode();
    merchDefaults.termOfUse = termOfUse;
    return merchDefaults;
  }

  if (isAnApiOrder()) {
    const merchDefaults = await checkApiForOrderTypeAndSetMode();
    merchDefaults.termOfUse = termOfUse;
    return merchDefaults;
  }

  defaultData.termOfUse = termOfUse;
  return defaultData;
};

watch(
  () => isBuy.value,
  (mode) => {
    if (mode) currentForm.value = BuyForm;
    else currentForm.value = SellForm;
  },
  { immediate: true },
);

onMounted(async () => {
  // reset order mode if it was previosuly set to NFT mode
  if (orderStore.isNFTMode) {
    orderStore.setActiveMode('BUY');
  }
  initialData.value = await setDefaults();
  resetAllProgress();
  useTitle(title);
});

</script>
<style lang="scss">
.user-message {
  text-align: center;
  color: var(--footer-text-color);
  margin-top: 2rem;
}
</style>
